import React, { useState } from 'react';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Send,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../../hooks/settings/use-settings';
import { usePublicContact } from '../../hooks/use-public-contact';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SeoHead } from '../../components/seo/seo-head';

export const ContactoPage: React.FC = () => {
  const { contactInfo } = useSettings();
  const { submitting, submitted, submitMessage, resetForm } = usePublicContact();

  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    asunto: 'Cotización por Volumen / Licitación',
    mensaje: '',
    _hp: '',
  });

  const phone = contactInfo?.primaryPhone || '+56 2 2923 8600';
  const email = contactInfo?.salesEmail || 'ventas@bartech.cl';
  const address = contactInfo?.addressStreet
    ? `${contactInfo.addressStreet}, ${contactInfo.addressCity}, ${contactInfo.addressRegion}`
    : 'Simón Bolívar 2389-A, Ñuñoa, Santiago de Chile';
  const hours = contactInfo?.businessHours || 'Lunes a Viernes: 09:00 a 18:30 hrs. (Continuado)';
  const whatsapp = contactInfo?.whatsappNumber || '+56 9 7138 0593';
  const whatsappClean = whatsapp.replace(/[^0-9]/g, '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre.trim() || !formData.email.trim() || !formData.mensaje.trim()) {
      toast.error('Por favor completa todos los campos requeridos (Nombre, Correo y Mensaje).');
      return;
    }

    const success = await submitMessage({
      fullName: formData.nombre.trim(),
      email: formData.email.trim(),
      phone: formData.telefono.trim() || null,
      company: formData.empresa.trim() || null,
      subject: formData.asunto,
      message: formData.mensaje.trim(),
      _hp: formData._hp || null,
    });

    if (success) {
      setFormData({
        nombre: '',
        empresa: '',
        email: '',
        telefono: '',
        asunto: 'Cotización por Volumen / Licitación',
        mensaje: '',
        _hp: '',
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <SeoHead
        title="Cotizaciones y Contacto B2B | Asesoría Técnica Especializada"
        description="Solicita cotización formal para equipamiento de retail, captura de datos, RFID, balanzas o terminales POS. Atención directa a empresas en todo Chile."
        canonicalUrl="https://bartech.cl/contacto"
      />
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto mb-12">
        <Badge variant="brand" size="sm" className="mb-2">
          Contacto & Asesoría Comercial
        </Badge>
        <h1 className="text-3xl sm:text-4xl font-black text-neutral-900 dark:text-white tracking-tight">
          Hablemos de tu Proyecto de Hardware
        </h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
          Puntos de venta, lectores de código de barra, impresoras térmicas o equipamiento comercial con soporte oficial.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* Columna Izquierda: Formulario de Contacto */}
        <div className="lg:col-span-7 bg-white dark:bg-neutral-900 p-6 sm:p-8 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
          {submitted ? (
            <div className="text-center py-12 space-y-4">
              <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 dark:text-white">
                ¡Solicitud Recibida Correctamente!
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">
                Hemos recibido tu consulta y asignado tu requerimiento al equipo comercial de BARTECH. Te responderemos a la brevedad.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={resetForm}
              >
                Enviar otra consulta
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Campo Honeypot Oculto anti-spam */}
              <input
                type="text"
                name="_hp"
                value={formData._hp}
                onChange={(e) => setFormData({ ...formData, _hp: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                style={{ display: 'none' }}
                aria-hidden="true"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  id="nombre"
                  label="Nombre y Apellido *"
                  placeholder="Ej. Carlos Valenzuela"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                />
                <Input
                  id="empresa"
                  label="Empresa o Razón Social (Opcional)"
                  placeholder="Ej. Comercial o Personal"
                  value={formData.empresa}
                  onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  id="email"
                  type="email"
                  label="Correo Electrónico *"
                  placeholder="carlos@correo.cl"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
                <Input
                  id="telefono"
                  type="tel"
                  label="Teléfono Móvil o Contacto"
                  placeholder="+56 9 1234 5678"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="asunto"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  Tipo de Requerimiento
                </label>
                <select
                  id="asunto"
                  value={formData.asunto}
                  onChange={(e) => setFormData({ ...formData, asunto: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 border border-neutral-300 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#33CC00]"
                >
                  <option value="Cotización de Equipos / Asesoría">Cotización de Equipos / Asesoría</option>
                  <option value="Proyecto de Punto de Venta / Sucursales">Proyecto de Punto de Venta / Sucursales</option>
                  <option value="Soporte Técnico / Garantía de Equipos">Soporte Técnico / Garantía de Equipos</option>
                  <option value="Venta Mayorista / Distribución">Venta Mayorista / Distribución</option>
                  <option value="Otra Consulta General">Otra Consulta General</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="mensaje"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  Detalle del Requerimiento o Equipos de Interés *
                </label>
                <textarea
                  id="mensaje"
                  rows={4}
                  placeholder="Describe los modelos de hardware que requieres, cantidades o características que necesitas..."
                  value={formData.mensaje}
                  onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 border border-neutral-300 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#33CC00] resize-none placeholder-neutral-400"
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting}
                className="w-full mt-2 bg-[#33CC00] hover:bg-[#2EB800] text-white border-none shadow-[0_4px_14px_rgba(51,204,0,0.3)] font-bold"
              >
                <Send className={`w-4 h-4 mr-1.5 ${submitting ? 'animate-spin' : ''}`} />
                <span>{submitting ? 'Enviando Consulta...' : 'Enviar Consulta a un Especialista'}</span>
              </Button>
            </form>
          )}
        </div>

        {/* Columna Derecha: Canales Directos & Ubicación */}
        <div className="lg:col-span-5 space-y-6">
          {/* Tarjeta WhatsApp Directo */}
          <div className="p-6 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#25D366] text-white flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-neutral-900 dark:text-white">
                  Atención Inmediata por WhatsApp
                </h3>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Respuesta ágil de un ejecutivo comercial
                </p>
              </div>
            </div>
            <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
              ¿Requieres asesoría técnica, fichas o una cotización urgente para el día de hoy? Chatea directamente con nuestro equipo.
            </p>
            <a
              href={`https://wa.me/${whatsappClean}?text=Hola%20BARTECH,%20deseo%20hacer%20una%20consulta%20comercial`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold text-xs shadow-sm transition-colors"
            >
              <span>Abrir WhatsApp ({whatsapp})</span>
            </a>
          </div>

          {/* Información Institucional */}
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 space-y-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-white">
              Casa Matriz & Laboratorio Técnico
            </h3>

            <div className="space-y-4 text-xs sm:text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Dirección:</strong>
                  <span className="text-neutral-600 dark:text-neutral-400">
                    {address}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Central Telefónica:</strong>
                  <span className="text-neutral-600 dark:text-neutral-400">{phone}</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Correo Comercial:</strong>
                  <span className="text-neutral-600 dark:text-neutral-400">{email}</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Horario de Operación:</strong>
                  <span className="text-neutral-600 dark:text-neutral-400">
                    {hours}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
